import { batch } from "solid-js";
import { reconcile } from "solid-js/store";
import Cookies from "js-cookie";

import { LocaleCanada } from "@repo/kikoff-models/Locale";
import User, { UserCanada, UserUsa } from "@repo/kikoff-models/User";
import t from "@repo/localization/string.macro";
import canadaRpc from "@repo/protobuf/api/canadaRpc";
import usRpc from "@repo/protobuf/api/usRpc";
import { User as UsUserPb } from "@repo/protobuf/gen/web/public/user_pb";
import Protobuf from "@repo/protobuf/utils";
import { invalidate } from "@repo/ui/global/signals";
import Address from "@repo/utils/Address";
import Chainable from "@repo/utils/Chainable";
import EventEmitter from "@repo/utils/EventEmitter";
import Locale from "@repo/utils/Locale";
import UDate from "@repo/utils/UDate";
import UFunction from "@repo/utils/UFunction";
import UPromise from "@repo/utils/UPromise";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import analytics, { track } from "~/analytics/client";

import { useOnboarding } from "./onboarding";

// Only ONBOARDING and ONBOARDED status are in use now
const routeByStatus: Record<User.Status, string> = {
  ONBOARDING: "/signup/info",
  ONBOARDED: "/dashboard",
};

const initialState = {
  authenticated: null as boolean | null,
  status: null as User.Status | null,
  pii: null as User.Pii.Readable | null,
  id: null as User.Id | null,
  impersonating: false,
  expectedLtv: null as number | null,
  mfaRequired: null as boolean | null,
  referrer: null as null | User.Referral,
};

export const { Provider, useUser, Context } = FeatureStore.init(
  "User",
  initialState,
  ([store, setStore], { attachAction, withLogger, once }) => {
    const [onboarding, setOnboarding] = useOnboarding();

    const events = once(
      "events",
      () =>
        new EventEmitter<[["loggedIn"], ["accountCreated"]]>().on(
          "accountCreated",
          () => {
            analytics.convert("Signup");
          }
        ).and
    );

    const accessors = {
      authenticated: () => store.authenticated,
      mfaRequired: () => store.mfaRequired,
      pii: () => store.pii,
      status: Object.assign(
        () => {
          if (onboarding.stage() === "pending_manual_idv_review")
            return "ONBOARDED";
          if (onboarding.stage() === "failed_manual_idv") return "ONBOARDED";
          if (onboarding.stage() === "manual_idv_needs_reupload")
            return "ONBOARDED";

          return store.status;
        },
        {
          route: () => routeByStatus[accessors.status()!],
        }
      ),
      id: () => store.id,
      impersonating: () => store.impersonating,
      expectedLtv: () => store.expectedLtv,
      referral: attachAction(
        () => store.referrer,
        () => actions.fetchReferrer()
      ),
    };

    const mutations = {
      update(user: User) {
        if (user.locale !== Locale.current) {
          Locale.current = user.locale;
          Cookies.set("locale", user.locale);
          invalidate();
          return;
        }

        analytics.identify(user.id);

        batch(() => {
          setStore("id", user.id);
          setStore("status", user.status);
          setStore("pii", reconcile(user.pii));
          if (user.expectedLtv)
            setStore("expectedLtv", user.expectedLtv || null);
        });
      },
    };

    const us = (() => {
      const fetch = Chainable.deferrable(
        ({ defer = false }) =>
          () =>
            usRpc.Account.getUserInfo({
              scopes: [UsUserPb.Scope.BASIC_INFO],
            }).then(({ user }) => {
              if (!defer) resolve();
              return resolve;

              function resolve() {
                mutations.update(UserUsa.normalize(user!));
              }
            })
      );

      const actions = {
        init: () =>
          usRpc.Account.getInitPage({
            scopes: [UsUserPb.Scope.BASIC_INFO],
          }).then(({ user, mfaRequired }) => {
            if (user) {
              mutations.update(UserUsa.normalize(user!));
              setStore("authenticated", !mfaRequired);
              setStore("mfaRequired", mfaRequired);
            } else {
              setStore("authenticated", false);
            }
          }),
        fetch,
        create: Chainable.deferrable(
          ({ defer = false }) =>
            (credentials: User.Credentials) => {
              track.onboarding.createAccount("attempt");

              return usRpc.Onboarding.createUser({
                ...credentials,
                referrerToken: User.Referral.Code.fetchPersisted(),
              })
                .CREATED((response) => ({ authenticated: false, response }))
                .AUTHENTICATED((response) => ({
                  authenticated: true,
                  response,
                }))
                .TAKEN(() => {
                  throw new Error(
                    "This email is already in use. Please try another or log in if you already have an account."
                  );
                })
                .elseThrow(t`Failed to create user.`)
                .then(async ({ authenticated, response }) => {
                  if (!authenticated) events.emit("accountCreated");
                  else if (!response.mfaRequired) events.emit("loggedIn");
                  track.onboarding.createAccount("success", {
                    authenticated,
                  });

                  // @ts-expect-error .state takes in either us or canada proto, resolves to never
                  setOnboarding.state(response.onboardingStatus!);
                  const resolveUser = await (defer ? fetch.defer() : fetch());
                  return {
                    authenticated,
                    resolveUser() {
                      batch(() => {
                        resolveUser();
                        setStore("authenticated", !response.mfaRequired);
                        setStore("mfaRequired", response.mfaRequired);
                      });
                    },
                  };
                })
                .catch((e) => {
                  track.onboarding.createAccount("failure", {
                    errors: [e?.message],
                  });
                  throw e;
                });
            }
        ),
        login: Chainable.deferrable(
          ({ defer = false }) =>
            (credentials: User.Credentials) => {
              track.login("attempt");

              return usRpc.Auth.login(credentials)
                .SUCCESS(({ user, mfaRequired }) => {
                  if (!mfaRequired) events.emit("loggedIn");
                  track.login("success");

                  if (!defer) resolve();
                  return resolve;

                  function resolve() {
                    batch(() => {
                      mutations.update(UserUsa.normalize(user!));
                      setStore("authenticated", !mfaRequired);
                      setStore("mfaRequired", mfaRequired);
                    });
                  }
                })
                .elseThrow(t`Failed to log in.`)
                .catch((e) => {
                  track.login("failure", {
                    errors: [e?.message],
                  });

                  throw e;
                });
            }
        ),
        googleSsoCallback(credential: string) {
          track.googleSso("attempt");

          return usRpc.GoogleSSO.callback({
            jwt: credential,
            referrerToken: User.Referral.Code.fetchPersisted(),
          })
            .CREATED(() => ({ authenticated: false }))
            .AUTHENTICATED(() => ({ authenticated: true }))
            .elseThrow(t`Failed to sign in with Google.`)
            .then(async ({ authenticated }) => {
              events.emit(authenticated ? "loggedIn" : "accountCreated");
              track.googleSso("success", { authenticated });

              const resolveUser = await actions.fetch.defer();

              batch(() => {
                resolveUser();
                setStore("authenticated", true);
              });

              return { authenticated };
            })
            .catch((e) => {
              track.googleSso("failure", { errors: [e.message] });
              throw e;
            });
        },
        mfa: {
          async init(purpose: User.Mfa.Purpose, method?: User.Mfa.Method) {
            await UPromise.delay(1000);
            return usRpc.Mfa.challenge({
              purpose: UserUsa.Mfa.Purpose.denormalize(purpose),
              carrier: method && UserUsa.Mfa.Method.denormalize(method),
            })
              .SUCCESS((response) => UserUsa.Mfa.normalizeResponse(response))
              .FAILURE(({ failureReason }) => {
                throw UserUsa.Mfa.Challenge.Failure.normalize(failureReason);
              })
              .elseThrow(t`Failed to initiate mfa.`);
          },
          verify: Object.assign(
            (...args: Parameters<typeof actions.mfa.verify.defer>) =>
              actions.mfa.verify.defer(...args).then((resolve) => resolve()),
            {
              defer(token: string, code: string) {
                return usRpc.Mfa.verify({
                  challengeToken: token,
                  verificationCode: code,
                })
                  .ACCEPTED(() => {
                    events.emit("loggedIn");

                    return () => {
                      batch(() => {
                        setStore("authenticated", true);
                        setStore("mfaRequired", false);
                      });
                    };
                  })
                  .INVALID(() => {
                    throw new User.Mfa.Verification.Invalid(t`Invalid code.`);
                  })
                  .REJECTED(() => {
                    throw new Error(
                      t`Incorrect code. Double-check the code and re-enter.`
                    );
                  })
                  .elseThrow(t`Failed to verify code.`);
              },
            }
          ),
        },
        fetchReferrer() {
          return usRpc.Referrals.getReferrerInfo({})
            .SUCCESS(({ firstName, referralAmount }) => {
              setStore("referrer", {
                referrer: {
                  firstName,
                },
                amountCents: referralAmount,
              });
            })
            .elseThrow(t`Failed to fetch referrer info.`);
        },
        logout() {
          setStore("authenticated", null);

          return usRpc.Auth.logout({})
            .SUCCESS(() => {
              analytics.anonymize();

              invalidate();
            })
            .elseThrow(t`Failed to log out.`);
        },
        fetchPromotion() {
          return usRpc.Account.getUserInfo({
            scopes: [UsUserPb.Scope.FEATURES],
          }).then(UFunction.todo);
        },
        updateEmail(credentials: User.Credentials) {
          return usRpc.Account.updateEmail(credentials)
            .SUCCESS(() => {
              return actions.init();
            })
            .TAKEN(() => {
              throw new Error(
                "This email is already in use. Please try another or log in if you already have an account."
              );
            })
            .elseThrow(t`Failed to update email.`);
        },
        updatePhone({ phone, password }: { phone: string; password: string }) {
          return (
            usRpc.Account.getPhoneValidationV2({ phone, password })
              .VALID(() => {
                return usRpc.Account.getPhoneCodeV2({
                  phone: phone,
                })
                  .SUCCESS(() => {
                    return actions.init();
                  })
                  .elseThrow(t`Failed to update phone.`);
              })
              // TODO: Add handling for additional mfa states
              .WAITING(() => {})
              .INVALID(() => {})
              .TAKEN(() => {})
              .elseThrow(t`Failed to update phone.`)
          );
        },
        confirmPhone({ phone, code }: { phone: string; code: string }) {
          return usRpc.Auth.updateForgotLoginPii({
            phone,
            phoneCode: code,
          })
            .SUCCESS(() => {
              return actions.init();
            })
            .elseThrow(t`Failed to confirm phone.`);
        },
        updateAddress({
          address,
          password,
        }: {
          address: Address;
          password: string;
        }) {
          return usRpc.Account.updateAddress({ ...address, password })
            .SUCCESS(() => {})
            .elseThrow(t`Failed to update address.`);
        },
        updatePassword({
          oldPassword,
          newPassword,
        }: {
          oldPassword: string;
          newPassword: string;
        }) {
          return usRpc.Account.updatePassword({
            oldPassword,
            newPassword,
            // TODO: Add mfa verification
            mfaVerificationProof: "00000",
          })
            .SUCCESS(() => {})
            .elseThrow(t`Failed to update password.`);
        },
        forgotPassword(email: string) {
          return usRpc.Auth.resetPassword({ contactPoint: email })
            .SUCCESS(() => {})
            .elseThrow(t`Failed to send reset password email.`);
        },
        resetPassword: UFunction.todo,
        sendConfirmationEmail() {
          return usRpc.Account.resendConfirmationEmail({})
            .SUCCESS(() => {})
            .elseThrow(t`Failed to send confirmation email.`);
        },
        confirmEmail(confirmationToken: string) {
          return usRpc.Auth.verifyEmail({ code: confirmationToken })
            .SUCCESS(() => {})
            .elseThrow(t`Failed to confirm email.`);
        },
        changeLocale(locale: Locale) {
          return usRpc.Account.updateLocale({
            locale,
          })
            .SUCCESS(() => actions.init())
            .elseThrow(t`Failed to change locale.`);
        },
      };

      return { actions };
    })();

    const canada = (() => {
      const actions = {
        init() {
          const start = Date.now();
          return canadaRpc.User.bootstrap({}).then(
            ({ user, serverTime, impersonating }) => {
              UDate.GlobalMod.shift(
                Protobuf.Timestamp.toNumber(serverTime!) -
                  Date.now() +
                  (Date.now() - start) / 2
              );

              if (!user) return setStore("authenticated", false);
              batch(() => {
                setStore("authenticated", true);
                setStore("impersonating", impersonating);
                mutations.update(UserCanada.normalize(user));
              });
            }
          );
        },
        fetch: Chainable.deferrable(
          ({ defer = false }) =>
            () =>
              canadaRpc.User.showUser({}).then(({ user }) => {
                if (!defer) resolve();
                return resolve;

                function resolve() {
                  mutations.update(UserCanada.normalize(user!));
                }
              })
        ),
        create: Chainable.deferrable(
          ({ defer = false }) =>
            (credentials: User.Credentials) => {
              track.onboarding.createAccount("attempt");

              return canadaRpc.Onboarding.createUser({
                ...credentials,
                locale: LocaleCanada.denormalize(Locale.current),
                promotionToken: Cookies.get("userLead-utm_content"),
              })
                .then(async ({ user, onboardingState, authenticated }) => {
                  events.emit(authenticated ? "loggedIn" : "accountCreated");
                  track.onboarding.createAccount("success", {
                    authenticated,
                  });

                  // @ts-expect-error .state takes in either us or canada proto, resolves to never
                  setOnboarding.state(onboardingState);
                  const resolveUser = () => {
                    mutations.update(UserCanada.normalize(user!));
                    setStore("authenticated", true);
                  };
                  if (!defer) resolveUser();
                  return {
                    authenticated,
                    resolveUser,
                  };
                })
                .catch((e) => {
                  track.onboarding.createAccount("failure", {
                    errors: e?.issues?.map(({ message }: any) => message) || [
                      e?.message,
                    ],
                  });
                  throw e;
                });
            }
        ),
        login: Chainable.deferrable(
          ({ defer = false }) =>
            (credentials: User.Credentials) => {
              track.login("attempt");

              return canadaRpc.Auth.login(credentials)
                .then(({ user }) => {
                  events.emit("loggedIn");
                  track.login("success");

                  if (!defer) resolve();
                  return resolve;

                  function resolve() {
                    mutations.update(UserCanada.normalize(user!));
                    setStore("authenticated", true);
                  }
                })
                .catch((e) => {
                  track.login("failure", {
                    errors: e?.issues?.map(({ message }: any) => message) || [
                      e?.message,
                    ],
                  });

                  throw e;
                });
            }
        ),
        logout() {
          setStore("authenticated", null);
          return canadaRpc.Auth.logout({}).then(() => {
            analytics.anonymize();

            invalidate();
          });
        },
        updateEmail: UFunction.todo,
        updatePhone: UFunction.todo,
        confirmPhone: UFunction.todo,
        updateAddress: UFunction.todo,
        updatePassword: UFunction.todo,
        googleSsoCallback: UFunction.todo,
        mfa: {
          init: UFunction.todo,
          verify: Object.assign(UFunction.todo, {
            defer: UFunction.todo,
          }),
        },
        fetchReferrer() {
          return Promise.resolve();
        },
        forgotPassword(email: string) {
          return canadaRpc.Auth.forgotPassword({ email });
        },
        resetPassword(password: string, token: string) {
          return canadaRpc.Auth.resetPassword({
            password,
            confirmationToken: token,
          });
        },
        sendConfirmationEmail() {
          return canadaRpc.User.sendConfirmationEmail({});
        },
        confirmEmail(confirmationToken: string) {
          return canadaRpc.User.confirmEmail({ confirmationToken });
        },
        changeLocale(locale: Locale) {
          return canadaRpc.User.changeLocale({
            locale: LocaleCanada.denormalize(locale),
          }).then(({ user }) => {
            mutations.update(UserCanada.normalize(user!));
          });
        },
      };

      return { actions };
    })();

    const actions = withLogger("action", {
      async pollEmailVerified() {
        await actions.fetch();
        if (!store.pii?.emailConfirmed) {
          await UPromise.delay(1000);
          await actions.pollEmailVerified();
        } else await setOnboarding.fetch();
      },
      ...(import.meta.env.API_ADAPTER === "canada"
        ? canada.actions
        : us.actions),
    });

    return { accessors, actions };
  }
);
